import React from 'react';
import { Card, Table, Pagination } from 'react-bootstrap';
import moment from 'moment';
import SubSecoesEmpreendimento from './SubSecoesEmpreendimento';

// Whitelist de colunas ordenáveis — alinhado com backend (TechSpec R4).
const COLUNAS_ORDENAVEIS = ['ge_nome', 'nome', 'codigo_familia', 'dt_lanc'];

const fmtNum = (valor, casas = 2) => {
  if (valor === null || valor === undefined || isNaN(valor)) return '-';
  return Number(valor).toLocaleString('pt-BR', {
    minimumFractionDigits: casas,
    maximumFractionDigits: casas,
  });
};

const fmtInt = (valor) => {
  if (valor === null || valor === undefined || isNaN(valor)) return '-';
  return Number(valor).toLocaleString('pt-BR', { maximumFractionDigits: 0 });
};

const fmtMesAnoLanc = (data) => {
  if (!data) return '-';
  const m = moment(data);
  return m.isValid() ? m.format('MM/YYYY') : '-';
};

const renderCidadeEstado = (item) => {
  const cidade = item.descricao_cidade || '';
  const estado = item.estado || '';
  if (!cidade && !estado) return '-';
  if (!estado) return cidade;
  return `${cidade} - ${estado}`;
};

const SortIcon = ({ ativo, direcao }) => {
  if (!ativo) {
    return <span style={{ marginLeft: 4, opacity: 0.4 }}>↕</span>;
  }
  return (
    <span style={{ marginLeft: 4 }}>
      {direcao === 'ASC' ? '↑' : '↓'}
    </span>
  );
};

const Paginador = ({ paginaAtual, totalPaginas, totalRegistros, pageSize, onMudarPagina }) => {
  if (totalPaginas <= 0) return null;
  const itens = [];
  const maxBotoes = 5;
  let inicio = Math.max(1, paginaAtual - Math.floor(maxBotoes / 2));
  let fim = Math.min(totalPaginas, inicio + maxBotoes - 1);
  if (fim - inicio < maxBotoes - 1) {
    inicio = Math.max(1, fim - maxBotoes + 1);
  }

  itens.push(
    <Pagination.First
      key="first"
      onClick={() => onMudarPagina(1)}
      disabled={paginaAtual === 1}
    />
  );
  itens.push(
    <Pagination.Prev
      key="prev"
      onClick={() => onMudarPagina(paginaAtual - 1)}
      disabled={paginaAtual === 1}
    />
  );
  if (inicio > 1) {
    itens.push(<Pagination.Ellipsis key="ellipsis-start" disabled />);
  }
  for (let n = inicio; n <= fim; n += 1) {
    itens.push(
      <Pagination.Item
        key={`p-${n}`}
        active={n === paginaAtual}
        onClick={() => onMudarPagina(n)}
      >
        {n}
      </Pagination.Item>
    );
  }
  if (fim < totalPaginas) {
    itens.push(<Pagination.Ellipsis key="ellipsis-end" disabled />);
  }
  itens.push(
    <Pagination.Next
      key="next"
      onClick={() => onMudarPagina(paginaAtual + 1)}
      disabled={paginaAtual === totalPaginas}
    />
  );
  itens.push(
    <Pagination.Last
      key="last"
      onClick={() => onMudarPagina(totalPaginas)}
      disabled={paginaAtual === totalPaginas}
    />
  );

  const inicioRegistro =
    totalRegistros > 0 ? (paginaAtual - 1) * pageSize + 1 : 0;
  const fimRegistro = Math.min(paginaAtual * pageSize, totalRegistros);

  return (
    <div className="d-flex justify-content-between align-items-center flex-wrap mt-3 mb-2">
      <div className="text-muted small mb-2">
        Mostrando {inicioRegistro} até {fimRegistro} de {totalRegistros}{' '}
        registros
      </div>
      <Pagination size="sm" className="mb-2">
        {itens}
      </Pagination>
    </div>
  );
};

const CabecalhoEmpreendimento = ({ item }) => {
  const linha1 = [
    { rotulo: 'GE', valor: item.codigo_grupo_empresa, key: 'ge' },
    { rotulo: 'Código', valor: item.codigo_familia, key: 'cod' },
    { rotulo: 'Nome', valor: item.nome_da_familia || '-', key: 'nome' },
    {
      rotulo: 'Cidade-Estado',
      valor: renderCidadeEstado(item),
      key: 'cid',
    },
    { rotulo: 'Estágio', valor: item.estagio_descricao || item.estagio || '-', key: 'est' },
    { rotulo: 'Dt. Lançamento', valor: fmtMesAnoLanc(item.dt_lanc), key: 'dt' },
  ];

  const linha2 = [
    { rotulo: 'Área', valor: fmtNum(item.area), key: 'area' },
    { rotulo: 'Aproveitamento', valor: fmtNum(item.aproveita), key: 'apr' },
    { rotulo: 'Lote Padrão', valor: fmtNum(item.tam_padrao), key: 'tp' },
    { rotulo: 'Lote Médio', valor: fmtNum(item.tam_medio), key: 'tm' },
    { rotulo: 'Qtde Lotes', valor: fmtInt(item.prev_lotes), key: 'pl' },
    { rotulo: 'Custo/Lote', valor: fmtNum(item.custo_lote), key: 'cl' },
    { rotulo: 'Valor Venda', valor: fmtNum(item.venda_lote), key: 'vl' },
    { rotulo: 'Pzo. Financ.', valor: fmtInt(item.pm_vendas), key: 'pmv' },
  ];

  return (
    <div className="px-3 py-2" style={{ backgroundColor: '#eaf2fb' }}>
      <div className="d-flex flex-wrap mb-1">
        {linha1.map((c) => (
          <div
            key={c.key}
            className="mr-3 mb-1"
            style={{ minWidth: '140px' }}
          >
            <div
              className="small text-muted"
              style={{ fontSize: '0.7rem' }}
            >
              {c.rotulo}
            </div>
            <div style={{ fontWeight: 600 }}>{c.valor}</div>
          </div>
        ))}
      </div>
      <div className="d-flex flex-wrap mb-1">
        {linha2.map((c) => (
          <div
            key={c.key}
            className="mr-3 mb-1"
            style={{ minWidth: '110px' }}
          >
            <div
              className="small text-muted"
              style={{ fontSize: '0.7rem' }}
            >
              {c.rotulo}
            </div>
            <div style={{ fontWeight: 600 }}>{c.valor}</div>
          </div>
        ))}
      </div>
      {item.obs && (
        <div className="mt-1">
          <div className="small text-muted" style={{ fontSize: '0.7rem' }}>
            Observações
          </div>
          <div style={{ whiteSpace: 'pre-wrap' }}>{item.obs}</div>
        </div>
      )}
    </div>
  );
};

const TabelaPrincipal = ({
  empreendimentos,
  totalRegistros,
  paginaAtual,
  pageSize,
  ordenacao,
  onMudarPagina,
  onMudarOrdenacao,
}) => {
  const totalPaginas = Math.max(1, Math.ceil(totalRegistros / pageSize));

  const handleClickColuna = (chave) => {
    if (!COLUNAS_ORDENAVEIS.includes(chave)) return;
    const novaDirecao =
      ordenacao.ordenarPor === chave && ordenacao.direcao === 'ASC'
        ? 'DESC'
        : 'ASC';
    onMudarOrdenacao({ ordenarPor: chave, direcao: novaDirecao });
  };

  // Empty state (RN-10) — não esconder o card; mostrar a mensagem inline.
  if (totalRegistros === 0) {
    return (
      <Card className="shadow-sm">
        <Card.Body className="text-center py-5 text-muted">
          Nenhum registro encontrado.
        </Card.Body>
      </Card>
    );
  }

  const colunasOrdenaveis = [
    { chave: 'ge_nome', titulo: 'GE / Nome (default)' },
    { chave: 'nome', titulo: 'Nome' },
    { chave: 'codigo_familia', titulo: 'Código' },
    { chave: 'dt_lanc', titulo: 'Dt. Lançamento' },
  ];

  return (
    <Card className="shadow-sm">
      <Card.Body>
        <div className="d-flex flex-wrap align-items-center mb-2">
          <span className="small text-muted mr-2">Ordenar por:</span>
          {colunasOrdenaveis.map((c) => {
            const ativo = ordenacao.ordenarPor === c.chave;
            return (
              <button
                key={c.chave}
                type="button"
                className={`btn btn-sm mr-2 mb-1 ${
                  ativo ? 'btn-primary' : 'btn-outline-secondary'
                }`}
                onClick={() => handleClickColuna(c.chave)}
              >
                {c.titulo}
                <SortIcon ativo={ativo} direcao={ordenacao.direcao} />
              </button>
            );
          })}
        </div>

        <Table size="sm" bordered className="mb-0">
          <thead>
            <tr style={{ backgroundColor: '#0747a6', color: 'white' }}>
              <th>Empreendimento</th>
            </tr>
          </thead>
          <tbody>
            {empreendimentos.map((item) => (
              <tr key={item.codigo_familia}>
                <td className="p-0">
                  <CabecalhoEmpreendimento item={item} />
                  <SubSecoesEmpreendimento item={item} />
                </td>
              </tr>
            ))}
          </tbody>
        </Table>

        <Paginador
          paginaAtual={paginaAtual}
          totalPaginas={totalPaginas}
          totalRegistros={totalRegistros}
          pageSize={pageSize}
          onMudarPagina={onMudarPagina}
        />
      </Card.Body>
    </Card>
  );
};

export default TabelaPrincipal;
